import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type {
  StorageOffersOverviewParams,
  StorageOffersResponse,
} from 'src/apis/storage/types';
import type { StorageServiceRootRequest } from 'src/apis/storage/use-storage-service-root';
import { useAxios, type Successful } from 'src/hooks/use-axios';

type HookProps = {
  storageServiceRoot: Successful<StorageServiceRootRequest>;
  params: StorageOffersOverviewParams;
};

type ExecuteProps = {
  url: string;
  params: StorageOffersOverviewParams;
};

export function usePublicStorageOffers({
  storageServiceRoot,
  params,
}: HookProps) {
  const storageOffers = useAxios(
    (axios, baseConfig, { url, params }: ExecuteProps) =>
      axios.request<StorageOffersResponse>({
        ...baseConfig,
        url,
        params,
      }),
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    // the link is guaranteed to be present in root response
    const offersLink =
      storageServiceRoot.response.data._links.getOffersOverview ||
      storageServiceRoot.response.data._links.getOffersOverviewPublic;

    const templatedUrl = urlTemplate.parse(offersLink.href).expand({});

    storageOffers.execute({
      url: templatedUrl,
      params,
    });
    // oxlint-disable-next-line exhaustive-deps
  }, [storageServiceRoot.response, params]);

  return storageOffers;
}

export type PublicStorageOffersRequest = ReturnType<
  typeof usePublicStorageOffers
>;
